import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { 
  FiUser, FiArrowRight, FiTerminal, FiLock, FiX, FiCheck, 
  FiCpu, FiMail, FiLogIn, FiShield, FiAlertCircle, FiArrowLeft 
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabase/supabase';

const Login = ({ onLogin, onAdminAccess, onBack }) => {
  const [authMode, setAuthMode] = useState('guest');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [agentName, setAgentName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  const [showAdminAuth, setShowAdminAuth] = useState(false);
  const [showForgotPass, setShowForgotPass] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');

  const handleStartGuestSession = (e) => {
    e.preventDefault();
    if (!agentName.trim()) return;
    setIsLoading(true);
    setTimeout(() => {
      onLogin({
        name: agentName,
        role: 'Guest Analyst',
        isGuest: true,
        avatar: `https://i.pravatar.cc/150?u=${agentName}`,
        tier2Access: false
      });
      setIsLoading(false);
    }, 800);
  };

  const handleMemberLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      if (data.user) {
        // Fetch extended profile including activation status and Tier 2 clearance
        const { data: profile } = await supabase
          .from('profiles_1776929082584')
          .select('is_verified, tier2_access, role, avatar_url, full_name')
          .eq('id', data.user.id)
          .single();

        if (profile && profile.is_verified === false) {
          await supabase.auth.signOut();
          throw new Error("Account deactivated or pending admin approval.");
        }

        onLogin({
          id: data.user.id,
          name: profile?.full_name || data.user.user_metadata?.full_name || data.user.email,
          email: data.user.email,
          role: profile?.role || 'Junior Analyst',
          isGuest: false,
          avatar: profile?.avatar_url || data.user.user_metadata?.avatar_url || `https://i.pravatar.cc/150?u=${data.user.id}`,
          tier2Access: profile?.tier2_access || false
        });
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin,
      });
      if (error) throw error;
      setSuccess('Recovery email sent. Check your inbox.');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminVerify = (e) => {
    e.preventDefault();
    if (adminPassword === 'admin123') {
      onAdminAccess();
    } else {
      setError('Invalid Administrative Credential');
      setTimeout(() => setError(''), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background blobs */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600 rounded-full blur-[120px]"></div>
      </div>

      <motion.button 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={onBack}
        className="absolute top-8 left-8 flex items-center space-x-2 text-slate-500 hover:text-white transition-all group z-20"
      >
        <SafeIcon icon={FiArrowLeft} className="group-hover:-translate-x-1 transition-transform" />
        <span className="text-[10px] font-black uppercase tracking-widest">Back to Website</span>
      </motion.button>

      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden z-10"
      >
        <div className="p-8 pb-0 text-center">
          <div className="inline-flex items-center justify-center bg-white px-8 py-5 rounded-2xl mb-4 shadow-lg shadow-blue-900/20">
            <img 
              src="https://media-manager-c.questera.ai/greta-media/c4cd7048ee42a7b0632cdf5aa1fa307c48d20f8ae20304e7af2c62d9e8bb8b361540d414afb44516657958e65ec582f9/images/aW1hZ2UvcG5n/ddfb2c5aeb3b376e9ab5a19c747ef4da.png" 
              alt="NetDesk Pro" 
              className="h-12 object-contain" 
            />
          </div>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-[0.2em] mb-6">Analyst Command Center</p>
          
          <div className="flex bg-slate-950 p-1.5 rounded-2xl border border-slate-800 mb-8">
            <button 
              onClick={() => { setAuthMode('guest'); setError(''); setShowForgotPass(false); }}
              className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${authMode === 'guest' ? 'bg-slate-800 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              GUEST
            </button>
            <button 
              onClick={() => { setAuthMode('member'); setError(''); }}
              className={`flex-1 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${authMode === 'member' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-300'}`}
            >
              MEMBER
            </button>
          </div>
        </div>

        <div className="p-8 pt-0">
          <AnimatePresence mode="wait">
            {authMode === 'guest' ? (
              <motion.form 
                key="guest-form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handleStartGuestSession}
                className="space-y-5 text-left"
              >
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Agent Name</label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input 
                      type="text" 
                      required
                      value={agentName}
                      onChange={(e) => setAgentName(e.target.value)}
                      placeholder="Identify yourself..."
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl py-3.5 pl-11 pr-4 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                    />
                  </div>
                </div>
                <div className="bg-slate-950 p-4 rounded-xl border border-slate-800/50">
                  <div className="flex items-center space-x-3 mb-2">
                    <SafeIcon icon={FiCpu} className="text-slate-500 text-sm" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Guest Context</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Local session only. Enroll your account later to sync XP and unlock senior certifications.
                  </p>
                </div>
                <button 
                  type="submit" 
                  disabled={isLoading || !agentName.trim()}
                  className="w-full group relative flex items-center justify-center px-6 py-4 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white rounded-xl font-black uppercase tracking-widest text-xs transition-all shadow-lg"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <span>Initialize Session</span>
                      <SafeIcon icon={FiArrowRight} className="ml-2 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </motion.form>
            ) : (
              <motion.form 
                key="member-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={showForgotPass ? handleForgotPassword : handleMemberLogin}
                className="space-y-4 text-left"
              >
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Email Address</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input 
                      type="email" 
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="analyst@netdesk.pro"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl py-3.5 pl-11 pr-4 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                {!showForgotPass && (
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Secure Password</label>
                    <div className="relative">
                      <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                      <input 
                        type="password" 
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-slate-950 border border-slate-800 rounded-xl py-3.5 pl-11 pr-4 text-white placeholder-slate-600 focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                      />
                    </div>
                  </div>
                )}

                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-[10px] font-bold text-center uppercase tracking-wider flex items-center justify-center space-x-2">
                    <SafeIcon icon={FiAlertCircle} />
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-500 text-[10px] font-bold text-center uppercase tracking-wider flex items-center justify-center space-x-2">
                    <SafeIcon icon={FiCheck} />
                    <span>{success}</span>
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full group relative flex items-center justify-center px-6 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-xl font-black uppercase tracking-widest text-xs transition-all shadow-xl shadow-blue-900/40"
                >
                  {isLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <>
                      <SafeIcon icon={showForgotPass ? FiMail : FiLogIn} className="mr-2" />
                      <span>{showForgotPass ? 'Send Recovery Link' : 'Authenticate Securely'}</span>
                    </>
                  )}
                </button>

                <div className="text-center">
                  <button 
                    type="button" 
                    onClick={() => { setShowForgotPass(!showForgotPass); setError(''); setSuccess(''); }}
                    className="text-[10px] font-black text-slate-500 hover:text-blue-400 uppercase tracking-widest transition-colors"
                  >
                    {showForgotPass ? 'Back to Login' : 'Forgot Password?'}
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>

        <div className="bg-slate-950/50 p-4 text-center border-t border-slate-800">
          <p className="text-[9px] text-slate-600 uppercase tracking-[0.3em] font-black">
            System Identity: NetDesk Kernel 4.2.0-STABLE
          </p>
        </div>
      </motion.div>

      <button 
        onClick={() => setShowAdminAuth(true)}
        className="mt-8 text-[11px] text-slate-600 hover:text-white transition-all flex items-center space-x-2 bg-slate-900 px-6 py-3 rounded-2xl border border-slate-800 hover:border-slate-700 shadow-xl"
      >
        <SafeIcon icon={FiShield} className="text-amber-500" />
        <span className="font-black uppercase tracking-[0.2em]">Administrative Console</span>
      </button>

      <AnimatePresence>
        {showAdminAuth && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-800 p-8 rounded-[2rem] shadow-2xl max-w-sm w-full relative"
            >
              <button 
                onClick={() => { setShowAdminAuth(false); setAdminPassword(''); setError(''); }}
                className="absolute top-6 right-6 text-slate-500 hover:text-white transition-colors"
              >
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
              <div className="text-center mb-6">
                <div className="w-12 h-12 bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-4 text-blue-500 border border-slate-700">
                  <SafeIcon icon={FiLock} className="text-xl" />
                </div>
                <h2 className="text-white font-black text-lg uppercase tracking-tight">Admin Terminal</h2>
                <p className="text-slate-400 text-[10px] mt-1 font-bold uppercase tracking-widest">Restricted Administrative Area</p>
              </div>
              <form onSubmit={handleAdminVerify} className="space-y-4 text-left">
                <input 
                  type="password" 
                  autoFocus
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Access Key Required"
                  className={`w-full bg-slate-950 border ${error ? 'border-red-500' : 'border-slate-800'} rounded-xl py-4 px-4 text-white text-center text-sm font-black outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
                />
                {error && <p className="text-red-500 text-[10px] font-bold text-center mt-2 uppercase tracking-wider">{error}</p>}
                <button 
                  type="submit"
                  className="w-full py-4 bg-white text-slate-900 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-100 transition-all flex items-center justify-center space-x-2 shadow-xl"
                >
                  <SafeIcon icon={FiCheck} />
                  <span>Verify & Enter</span>
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Login;